/**
 * ObstacleManager.js
 */

import { COLORS, LINES, OBSTACLE } from "../Global/materials";
import * as THREE from "three";
import { ObstacleTypes, POLYGONS, VARS } from "../Global/variables";
import { addExtrude, updateObstacleShape } from "./Modifications";
import { addOffset, createCirGeo, createRecGeo } from "./Shapes";
import { createSegmentPlane } from "../Global/functions";
import { sceneManager } from "../SetUps/SceneManager";
import { pushUndoAction } from "../Global/UndoManager";

const obstacleSnapRaycaster = new THREE.Raycaster();

function snapPointToModelSurface(point, plane, meshes) {
  if (!meshes || !meshes.length || !plane) return point.clone();

  const normal = plane.normal.clone().normalize();
  const dirDown = normal.clone().negate();
  const dirUp = normal.clone();

  const origin1 = point.clone().add(normal.clone().multiplyScalar(2));
  obstacleSnapRaycaster.set(origin1, dirDown);
  let hits = obstacleSnapRaycaster.intersectObjects(meshes, true);
  if (hits.length) return hits[0].point.clone();

  const origin2 = point.clone().add(normal.clone().negate().multiplyScalar(2));
  obstacleSnapRaycaster.set(origin2, dirUp);
  hits = obstacleSnapRaycaster.intersectObjects(meshes, true);
  if (hits.length) return hits[0].point.clone();

  return point.clone();
}


function findTopSegmentUnderRay(ray) {
  const origin = ray.origin.clone();
  let best = null;

  for (let idx = 0; idx < POLYGONS.length; idx++) {
    const plane = createSegmentPlane(idx);
    if (!plane) continue;

    const hit = new THREE.Vector3();
    if (!ray.intersectPlane(plane, hit)) continue;

    const poly = getSegmentPolygon2D(idx);
    if (!poly) continue;

    const p2d = new THREE.Vector2(hit.x, hit.y);
    if (!pointInPolygon2D(p2d, poly)) continue;

    const dist = origin.distanceTo(hit);
    if (!best || dist < best.distance) {
      best = { segmentIndex: idx, hitPoint: hit.clone(), distance: dist };
    }
  }

  return best; // or null
}

function isFirstClickAllowedByRay(ray, parentSegmentIndex) {
  const hitSeg = findTopSegmentUnderRay(ray);

  // outside all segments ✅
  if (!hitSeg) return true;

  // only allow if the topmost segment under cursor is the parent ✅
  return hitSeg.segmentIndex === parentSegmentIndex;
}

function snapPointsToModelSurface(points, segmentIndex) {
  const meshes = VARS.Meshes || [];
  if (!meshes.length || !points || !points.length) return points;

  const plane = createSegmentPlane(segmentIndex);
  if (!plane) return points;

  return points.map((p) => snapPointToModelSurface(p, plane, meshes));
}

function getSegmentPolygon2D(segmentIndex) {
  const segment = POLYGONS[segmentIndex];
  if (!segment || !segment.edges || !segment.edges.length) return null;

  const segPoints2D = [];
  segment.edges.forEach((edge) => {
    if (edge.point1) segPoints2D.push(new THREE.Vector2(edge.point1.x, edge.point1.y));
    if (edge.point2) segPoints2D.push(new THREE.Vector2(edge.point2.x, edge.point2.y));
  });

  const uniquePoints = [];
  for (let p of segPoints2D) {
    if (!uniquePoints.some((u) => u.distanceTo(p) < 1e-6)) uniquePoints.push(p);
  }
  if (!uniquePoints.length) return null;

  const center = uniquePoints
    .reduce((acc, p) => acc.add(p), new THREE.Vector2())
    .divideScalar(uniquePoints.length);

  uniquePoints.sort(
    (a, b) =>
      Math.atan2(a.y - center.y, a.x - center.x) - Math.atan2(b.y - center.y, b.x - center.x)
  );

  return uniquePoints;
}


function debugRayHitObstaclePlacement(ray, parentSegmentIndex) {
  // Try to find a segment under the ray (closest hit)
  const hitSeg = findTopSegmentUnderRay(ray);

  // If you also want to log if the ray hits ANYTHING else (non-segment scene objects),
  // you can optionally add "other hit" info here (see section 3).
  const hitInfo = {
    hitType: hitSeg ? "segment" : "none",
    segmentIndex: hitSeg ? hitSeg.segmentIndex : null,
    worldPoint: hitSeg ? hitSeg.hitPoint : null,
    isParentSegment: hitSeg ? hitSeg.segmentIndex === parentSegmentIndex : false,
  };

  return hitInfo;
}

function pointInPolygon2D(point, polygon) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x,
      yi = polygon[i].y;
    const xj = polygon[j].x,
      yj = polygon[j].y;

    const intersect =
      yi > point.y !== yj > point.y && point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;

    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * ✅ NEW: Return all segments (indices) whose 2D polygon contains the worldPoint (XY)
 */
function getContainingSegments(worldPoint) {
  const p2d = new THREE.Vector2(worldPoint.x, worldPoint.y);
  const hits = [];

  for (let i = 0; i < POLYGONS.length; i++) {
    const poly = getSegmentPolygon2D(i);
    if (!poly) continue;
    if (pointInPolygon2D(p2d, poly)) hits.push(i);
  }
  return hits;
}

/**
 * ✅ NEW RULE:
 * First click is allowed if it's:
 *  - inside the parent segment, OR
 *  - outside all segments
 * but NOT inside any other segment besides the parent.
 */

function setCursor(cursor) {
  const dom = sceneManager?.renderer?.domElement;
  if (dom) dom.style.cursor = cursor;
}

class ObstacleManager {
  constructor(
    scene,
    camera,
    obstacleType,
    stopDrawing,
    updateObstacleList,
    selectedIndices,
    obsIndex = null
  ) {
    this.scene = scene;
    this.camera = camera;
    this.startPoint = null;
    this.endPoint = null;
    this.plane = new THREE.Plane();
    this.box = OBSTACLE.box;
    this.circle = OBSTACLE.circle;
    this.type = obstacleType;
    this.isDrawingStarted = false;
    this.rayCaster = new THREE.Raycaster();
    this.stopDrawing = stopDrawing;
    this.updateObstacleList = updateObstacleList;
    this.obsIndex = obsIndex;
    this._lastHoverAllowed = null;
this._lastHoverHitKey = null;


    if (typeof selectedIndices === "object" && selectedIndices !== null) {
      this.selectedSegment = selectedIndices.segmentIndex;
    } else {
      this.selectedSegment = selectedIndices;
    }
  }

  handleMouseDown(point) {
    if (this.selectedSegment == null) return;

    if (!this.isDrawingStarted) {
      // Always draw on the parent plane
      this.plane = createSegmentPlane(this.selectedSegment);
      if (!this.plane) return;

      const hit = new THREE.Vector3();
      this.rayCaster.setFromCamera(point, this.camera);

      if (!this.rayCaster.ray.intersectPlane(this.plane, hit)) {
        setCursor("not-allowed");
        return;
      }

      // ✅ CHANGED: allow start anywhere EXCEPT inside other segments
      const allowed = isFirstClickAllowedByRay(this.rayCaster.ray, this.selectedSegment);
if (!allowed) {
  setCursor("not-allowed");
  return;
}


      this.startPoint = point;
      this.isDrawingStarted = true;
      setCursor("crosshair");
    } else {
      // ✅ Finalize anywhere (even completely outside parent) — no segment overlap restriction
      switch (this.type) {
        case ObstacleTypes.RECTANGLE:
          this.box.visible = false;
          this.completeShape(this.points, 0, 6, COLORS.maroon, "rectangle");
          break;
        case ObstacleTypes.CIRCLE:
          this.circle.visible = false;
          this.completeShape(this.points, 0, 6, COLORS.maroon, "circle");
          break;
      }

      this.isDrawingStarted = false;
      this.stopDrawing();
      setCursor("auto");
    }
  }

  handleMouseMove(point) {
  // ✅ Cursor hint ONLY for the first click eligibility
  if (!this.isDrawingStarted && this.selectedSegment != null) {
    const hit = new THREE.Vector3();
    this.rayCaster.setFromCamera(point, this.camera);

    // Use your ray-based segment hit
    const info = debugRayHitObstaclePlacement(this.rayCaster.ray, this.selectedSegment);

    // Determine "allowed" using your new rule
    const allowed = info.hitType === "none" || info.isParentSegment;

    // Build a stable key so we log only when something meaningful changes
    const hitKey = `${allowed}|${info.hitType}|${info.segmentIndex ?? "null"}|${
      info.worldPoint
        ? `${info.worldPoint.x.toFixed(3)},${info.worldPoint.y.toFixed(3)},${info.worldPoint.z.toFixed(3)}`
        : "null"
    }`;

    if (hitKey !== this._lastHoverHitKey) {
      this._lastHoverHitKey = hitKey;

      // console.log("🟦 Obstacle hover ray debug:", {
      //   allowed,
      //   parentSegment: this.selectedSegment,
      //   hitType: info.hitType,                 // "segment" | "none"
      //   hitSegment: info.segmentIndex,         // number | null
      //   isParentSegment: info.isParentSegment, // boolean
      //   worldPoint: info.worldPoint
      //     ? { x: info.worldPoint.x, y: info.worldPoint.y, z: info.worldPoint.z }
      //     : null,
      // });
    }

    setCursor(allowed ? "crosshair" : "not-allowed");
  }

  // existing drawing logic...
  if (this.isDrawingStarted) {
    this.endPoint = point;

    switch (this.type) {
      case ObstacleTypes.RECTANGLE:
        this.box.visible = true;
        this.drawRectangle();
        break;
      case ObstacleTypes.CIRCLE:
        this.circle.visible = true;
        this.drawCircle();
        break;
    }

    setCursor("crosshair");
  }
}


  drawRectangle() {
    let { points, g } = createRecGeo(
      this.startPoint,
      this.endPoint,
      this.plane,
      this.rayCaster,
      this.camera
    );

    const center = new THREE.Vector3();
    g.computeBoundingBox();
    g.boundingBox.getCenter(center);

    this.box.geometry.dispose();
    this.box.geometry = g;
    center.normalize();
    this.box.geometry.attributes.position.needsUpdate = true;
    this.box.renderOrder = 1;

    this.points = points;
  }

  drawCircle() {
    let { points, geo } = createCirGeo(
      this.startPoint,
      this.endPoint,
      this.plane,
      this.rayCaster,
      this.camera
    );

    const line = new THREE.Line(geo, LINES.magentaMaterial.clone());

    this.circle.geometry.dispose();
    this.circle.geometry = line.geometry;
    this.circle.material = line.material;

    this.points = points;
  }

  completeShape(points, height, offset, color, type) {
    if (!points || !points.length) return;

    // ✅ DO NOT snap on creation. Snapping happens only on explicit flush (tick icon).
    const shapePoints = points.map((p) => new THREE.Vector2(p.x, p.y));
    const shape = new THREE.Shape(shapePoints);

    let extrudeMesh = addExtrude(points, shape, height, color, this.scene);
    const ret = addOffset(extrudeMesh, points, type, shape, offset, height, this.selectedSegment);

    this.addObstacle(type, ret.shape, ret.points, ret.mesh, height, offset, color);
  }

  addObstacle(type, shape, points, extrudeMesh, height, offset, color) {
    let parentSegment = this.selectedSegment;

    if (this.obsIndex !== null) {
      const existing = POLYGONS[this.selectedSegment]?.obstructions?.[this.obsIndex];
      if (existing && typeof existing.parentSegment === "number") {
        parentSegment = existing.parentSegment;
      }
    }

    let obs = {
      type: type,
      polyShape: shape,
      cords: points,
      mesh: extrudeMesh,
      height: height,
      ogColor: color,
      offset: offset,
      linkedSegments: [],
      parentSegment: parentSegment,
      isFlushed: false,
    };

    console.log("🟩 NEW OBSTACLE CREATED (NO FLUSH) →", {
      parentSegment,
      cords: points.map((p) => ({ x: p.x, y: p.y, z: p.z })),
    });

    if (this.obsIndex === null) {
      POLYGONS[this.selectedSegment].obstructions.push(obs);

      obs.linkedSegments = [parentSegment];

      let uiData = { type: type, height: height, offset: offset };
      this.updateObstacleList({
        newObstacle: uiData,
        segmentIndex: this.selectedSegment,
      });

      const parentSegmentIndex = parentSegment;
      const obstacleRef = obs;

      pushUndoAction({
        label: "Add obstacle",
        undo: () => {
          const obstruction = obstacleRef;
          if (!obstruction) return;

          if (obstruction.mesh) {
            const mesh = obstruction.mesh;
            if (mesh.parent) mesh.parent.remove(mesh);
            if (mesh.geometry) mesh.geometry.dispose();
            if (mesh.material) mesh.material.dispose();
          }

          if (Array.isArray(obstruction.linkedSegments) && obstruction.linkedSegments.length) {
            obstruction.linkedSegments.forEach((idx) => {
              const seg = POLYGONS[idx];
              if (!seg || !Array.isArray(seg.obstructions)) return;
              seg.obstructions = seg.obstructions.filter((o) => o !== obstruction);
            });
          } else {
            const seg = POLYGONS[parentSegmentIndex];
            if (seg && Array.isArray(seg.obstructions)) {
              seg.obstructions = seg.obstructions.filter((o) => o !== obstruction);
            }
          }
        },
      });
    } else {
      const old = POLYGONS[this.selectedSegment]?.obstructions?.[this.obsIndex];
      if (old && typeof old.isFlushed === "boolean") {
        obs.isFlushed = old.isFlushed;
      }
      if (old && Array.isArray(old.linkedSegments)) {
        obs.linkedSegments = old.linkedSegments.slice();
      } else {
        obs.linkedSegments = [parentSegment];
      }
      POLYGONS[this.selectedSegment].obstructions.splice(this.obsIndex, 1, obs);
    }
  }
}

/**
 * Determines which roof segments an obstacle overlaps based on edge boundaries.
 */
function getIntersectingSegmentsFromEdges(obstaclePoints) {
  const overlappingSegments = [];
  const obstacle2D = obstaclePoints.map((p) => new THREE.Vector2(p.x, p.y));

  const obsMinX = Math.min(...obstacle2D.map((p) => p.x));
  const obsMaxX = Math.max(...obstacle2D.map((p) => p.x));
  const obsMinY = Math.min(...obstacle2D.map((p) => p.y));
  const obsMaxY = Math.max(...obstacle2D.map((p) => p.y));

  POLYGONS.forEach((segment, idx) => {
    if (!segment || !segment.edges || segment.edges.length === 0) return;

    const segPoints2D = [];
    segment.edges.forEach((edge) => {
      if (edge.point1) segPoints2D.push(new THREE.Vector2(edge.point1.x, edge.point1.y));
      if (edge.point2) segPoints2D.push(new THREE.Vector2(edge.point2.x, edge.point2.y));
    });

    const uniquePoints = [];
    for (let p of segPoints2D) {
      if (!uniquePoints.some((u) => u.distanceTo(p) < 1e-6)) uniquePoints.push(p);
    }
    if (!uniquePoints.length) return;

    const center = uniquePoints
      .reduce((acc, p) => acc.add(p), new THREE.Vector2())
      .divideScalar(uniquePoints.length);
    uniquePoints.sort(
      (a, b) =>
        Math.atan2(a.y - center.y, a.x - center.x) - Math.atan2(b.y - center.y, b.x - center.x)
    );

    const segMinX = Math.min(...uniquePoints.map((p) => p.x));
    const segMaxX = Math.max(...uniquePoints.map((p) => p.x));
    const segMinY = Math.min(...uniquePoints.map((p) => p.y));
    const segMaxY = Math.max(...uniquePoints.map((p) => p.y));

    const bboxIntersects =
      obsMinX <= segMaxX && obsMaxX >= segMinX && obsMinY <= segMaxY && obsMaxY >= segMinY;
    if (!bboxIntersects) return;

    if (polygonOverlap2D(uniquePoints, obstacle2D)) {
      overlappingSegments.push(idx);
    }
  });

  return overlappingSegments;
}

function polygonOverlap2D(polyA, polyB) {
  return polyA.some((p) => pointInPolygon2D(p, polyB)) || polyB.some((p) => pointInPolygon2D(p, polyA));
}

/**
 * ✅ Flush by index (reliable).
 * - snaps to surface (ONLY HERE)
 * - rebuilds geometry once
 * - then attaches to intersecting segments
 */
export function flushObstacleToSceneByIndex(segmentIndex, obstacleIndex) {
  const obstruction = POLYGONS?.[segmentIndex]?.obstructions?.[obstacleIndex];
  if (!obstruction) return;

  if (obstruction.isFlushed) return;

  const parent = obstruction.parentSegment;
  if (typeof parent !== "number") return;

  // ✅ Snap only now (explicit flush)
  const snapped = snapPointsToModelSurface(obstruction.cords, parent);

  // IMPORTANT: update by the index inside the PARENT list (owner list)
  const parentIdx = POLYGONS?.[parent]?.obstructions?.indexOf(obstruction);
  if (parentIdx === -1) return;

  // Rebuild mesh/offset to match snapped points
  updateObstacleShape(parent, parentIdx, snapped);

  // Compute overlaps from updated cords
  const overlapping = getIntersectingSegmentsFromEdges(obstruction.cords).filter((idx) => idx !== parent);

  const linked = new Set([parent, ...overlapping]);

  overlapping.forEach((idx) => {
    const seg = POLYGONS[idx];
    if (!seg || !Array.isArray(seg.obstructions)) return;
    if (!seg.obstructions.includes(obstruction)) {
      seg.obstructions.push(obstruction);
    }
  });

  obstruction.linkedSegments = Array.from(linked);
  obstruction.isFlushed = true;

  console.log("✅ Flushed obstacle to scene. linkedSegments:", obstruction.linkedSegments);
}

/**
 * Backward-compatible
 */
export function flushObstacleToScene(obstruction) {
  if (!obstruction) return;
  const parent = obstruction.parentSegment;
  if (typeof parent !== "number") return;

  const idx = POLYGONS?.[parent]?.obstructions?.indexOf(obstruction);
  if (idx === -1) return;

  flushObstacleToSceneByIndex(parent, idx);
}

export function updateLinkedSegmentsAfterMove(obstruction) {
  if (!obstruction || !obstruction.cords) return;

  if (!obstruction.isFlushed) {
    const parent = obstruction.parentSegment;
    if (typeof parent === "number") {
      POLYGONS.forEach((seg, idx) => {
        if (!seg || !Array.isArray(seg.obstructions)) return;
        if (idx === parent) {
          if (!seg.obstructions.includes(obstruction)) seg.obstructions.push(obstruction);
        } else {
          seg.obstructions = seg.obstructions.filter((o) => o !== obstruction);
        }
      });

      obstruction.linkedSegments = [parent];
    }
    return;
  }

  const prevLinked = [];
  POLYGONS.forEach((seg, idx) => {
    if (!seg || !Array.isArray(seg.obstructions)) return;
    if (seg.obstructions.includes(obstruction)) prevLinked.push(idx);
  });

  let overlapping = getIntersectingSegmentsFromEdges(obstruction.cords);

  const parent = obstruction.parentSegment;
  if (typeof parent === "number" && !overlapping.includes(parent)) {
    overlapping.push(parent);
  }

  prevLinked.forEach((idx) => {
    if (!overlapping.includes(idx)) {
      const seg = POLYGONS[idx];
      if (!seg || !Array.isArray(seg.obstructions)) return;
      seg.obstructions = seg.obstructions.filter((o) => o !== obstruction);
    }
  });

  overlapping.forEach((idx) => {
    const seg = POLYGONS[idx];
    if (!seg || !Array.isArray(seg.obstructions)) return;
    if (!seg.obstructions.includes(obstruction)) {
      seg.obstructions.push(obstruction);
    }
  });

  obstruction.linkedSegments = [...overlapping];
}

export default ObstacleManager;
